import { useCallback, useEffect, useMemo, useRef, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { useCriticalSound, useCriticalTabTitle, useOpenCriticals, type OpenCriticals } from '@/hooks/useCriticalAlerts'
import { useAcknowledgeAllCritical, useAcknowledgeCritical } from '@/hooks/useAcknowledgeCritical'
import { useCriticalTabBadge } from '@/hooks/useCriticalTabBadge'
import { useCriticalHistoryRefresh } from '@/hooks/useCriticalHistoryRefresh'  // #392
import { useCriticalSoundSettingsState } from '@/hooks/useCriticalSoundSettings'
import { useAuthStore } from '@/store/authStore'
import { clearHiddenPopups, hidePopupId, isPopupHidden, MAX_POPUPS, useHiddenPopupsVersion } from '@/lib/criticalPopups'
import {
  CriticalAlertsContext, CriticalQuietLeftContext, NO_CRITICAL_ALERTS, type CriticalAlertsValue,
} from './CriticalAlertsContext'

const NONE: OpenCriticals = { open: [], total: 0 }
const NO_POPUPS = { popups: [] as OpenCriticals['open'], morePopups: 0 }

/**
 * #294 redesign (plan C11) — the ONE critical alerts controller, mounted once in MainLayout around the
 * content: the open-critical query, the critical sound (useCriticalSound, which also holds this tab's sound
 * leadership), the tab title "CRITICAL (N) · DeterCam" and the tab icon badge. The alert bar and the popup
 * stack only read what it provides (CriticalAlertsContext; the Quiet countdown in CriticalQuietLeftContext).
 *
 * "Loaded" = the open list has data — also when that data came from a WebSocket placeholder while the list
 * request fails (Review Focus 2: the bar, popups, title and sound still carry the alert) — AND the super
 * admin's sound rules have been read or have failed (review 2026-10-12: with the defaults, an alert the real
 * rules have ended would get a popup, latched for good, and a sound). The bar and the title do not wait. Signed out, it
 * reads as nothing open, so nothing shows and the sound stops at once (logout, CP 294 edge 13).
 */
export function CriticalAlertsProvider({ children }: { children: ReactNode }) {
  const { t } = useTranslation('common')
  const signedIn = useAuthStore((s) => s.isAuthenticated)
  const query = useOpenCriticals()
  const { open, total } = (signedIn && query.data) || NONE
  const { settled: soundRulesSettled } = useCriticalSoundSettingsState()
  const loaded = signedIn && query.data !== undefined && soundRulesSettled
  const fetchedAt = query.dataUpdatedAt
  const { quiet, quietLeft, soundBlocked, insideWindow } = useCriticalSound(open, loaded, fetchedAt)
  useCriticalTabTitle(total, t('critical.tabTitle', { count: total }))
  useCriticalTabBadge(total)
  // #392 — the open list's ids changed (its 30 s poll): refresh the Critical tab and Favourites too
  useCriticalHistoryRefresh()

  const ack = useAcknowledgeCritical()
  const ackAll = useAcknowledgeAllCritical()
  const ackMutate = ack.mutate
  const ackAllMutate = ackAll.mutate
  const acknowledge = useCallback((id: number) => ackMutate(id), [ackMutate])
  const acknowledgeAll = useCallback(() => {
    if (open.length > 0) ackAllMutate(open)
  }, [ackAllMutate, open])
  const acknowledgingId = ack.isPending && typeof ack.variables === 'number' ? ack.variables : null

  // The popups (CP 294 Section 1.1 / edge 2). An alert gets one while it is inside its sound window; once
  // shown it LATCHES (stays until acknowledged, closed with ✕, or no longer open) even after the window
  // ends. Newest first, at most 3. "+ N more" (review 2026-10-12): whenever at least one popup is on screen and
  // more alerts are open than popups shown, N = all open − popups shown (closed ones and those outside their
  // window included: they are open, just not shown).
  const hiddenVersion = useHiddenPopupsVersion()
  const latched = useRef(new Set<number>())
  useEffect(() => {
    if (!signedIn) {
      latched.current.clear()
      clearHiddenPopups()
    }
  }, [signedIn])
  // Logout unmounts the layout in the same render (ProtectedRoute), so the effect above never sees it signed
  // out: forget the closed popups when it unmounts signed out (lib/criticalPopups also watches the auth
  // store). A remount while still signed in keeps them (CP 294 Section 1.1: until the page is reloaded).
  useEffect(() => () => {
    if (!useAuthStore.getState().isAuthenticated) clearHiddenPopups()
  }, [])
  const { popups, morePopups } = useMemo(() => {
    if (!loaded) return NO_POPUPS
    const eligible = open.filter((e) => !isPopupHidden(e.id) && (latched.current.has(e.id) || insideWindow(e)))
    const shown = eligible.slice(0, MAX_POPUPS)
    for (const e of shown) latched.current.add(e.id)   // idempotent
    return { popups: shown, morePopups: shown.length > 0 && total > shown.length ? total - shown.length : 0 }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- hiddenVersion: the closed set is module-level
  }, [loaded, open, total, insideWindow, hiddenVersion])
  const hidePopup = hidePopupId

  const value = useMemo<CriticalAlertsValue>(() => ({
    ...NO_CRITICAL_ALERTS,
    open,
    total,
    fetchedAt,
    insideWindow,
    quiet,
    soundBlocked,
    acknowledge,
    acknowledgingId,
    acknowledgeAll,
    acknowledgingAll: ackAll.isPending,
    popups,
    morePopups,
    hidePopup,
  }), [open, total, fetchedAt, insideWindow, quiet, soundBlocked, acknowledge, acknowledgingId,
    acknowledgeAll, ackAll.isPending, popups, morePopups, hidePopup])

  // the Quiet countdown in its own context: it ticks every second and only the bar reads it
  return (
    <CriticalAlertsContext.Provider value={value}>
      <CriticalQuietLeftContext.Provider value={quietLeft}>{children}</CriticalQuietLeftContext.Provider>
    </CriticalAlertsContext.Provider>
  )
}
